import {
  isPslfEligiblePlanKey,
  type SortPlansOutput,
  type SortPlansPayload,
  type SortedPlanRow,
} from 'tio-common/utils/strategy-finder-0726';
import type {
  GeneratePaymentPlanPayload,
  GeneratePaymentPlanResponse,
  PaymentPlanRow,
} from 'tio-data/requests/payment-plan-explorer';

// A demo stand-in for the sort-plans engine, not tio-api's rules: HHS 2025 poverty line, 48 states.
export const DEMO_POVERTY_GUIDELINE_CENTS = { base: 1_565_000, perAdditionalPerson: 550_000 };

export const PLAN_TITLES: Record<string, string> = {
  tenYearsStandard: 'Standard',
  extendedPlan: 'Extended',
  ibr: 'Income-Based (IBR)',
  paye: 'Pay As You Earn (PAYE)',
  icr: 'Income-Contingent (ICR)',
  repaymentAssistancePlan: 'Repayment Assistance Plan (RAP)',
};

const INCOME_DRIVEN_PLANS = ['ibr', 'paye', 'icr', 'repaymentAssistancePlan'];
const IDR_0726_PLANS = new Set(INCOME_DRIVEN_PLANS);
const NEW_BORROWER_COHORTS = ['new_ibr_cohort', 'obbb_cohort'];
const EXTENDED_PLAN_MIN_BALANCE_CENTS = 3_000_000;
const TLF_FORGIVENESS_MONTH = 60;

export interface SimulatePlanInput {
  balanceCents: number;
  rateAnnual: number;
  agiCents: number;
  agiGrowthRate: number;
  adults: number;
  children: number;
  paidMonths: number;
  firstLoanTime: string;
  lastLoanTime: string;
  plan: string;
  pslf: boolean;
  tlfAmountCents: number;
  multiplier: number;
}

const finite = (value: unknown) =>
  typeof value === 'number' && Number.isFinite(value) ? value : 0;

const amortize = (balance: number, months: number, monthlyRate: number) =>
  monthlyRate === 0
    ? Math.ceil(balance / months)
    : Math.round((balance * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -months)));

function rapPercent(agiCents: number) {
  if (agiCents <= 1_000_000) {
    return 0;
  }
  return Math.min(10, Math.ceil(agiCents / 1_000_000) - 1) / 100;
}

function zeroRow(): PaymentPlanRow {
  return {
    month: 0,
    payment: 0,
    cumulative_payment: 0,
    employer_payment: 0,
    subsidy_amount: 0,
    forgiveness_amount: 0,
    balance: 0,
  };
}

export function simulatePlan(input: SimulatePlanInput): {
  rows: PaymentPlanRow[];
  summary: SortedPlanRow;
} {
  const startBalance = Math.max(0, Math.round(finite(input.balanceCents)));
  const monthlyRate = Math.max(0, finite(input.rateAnnual)) / 12;
  const agi = Math.max(0, finite(input.agiCents));
  const agiGrowthRate = finite(input.agiGrowthRate);
  const children = Math.max(0, Math.floor(finite(input.children)));
  const householdSize = Math.max(1, Math.max(0, Math.floor(finite(input.adults))) + children);
  const paidMonths = Math.max(0, Math.floor(finite(input.paidMonths)));
  const multiplier = finite(input.multiplier) > 0 ? finite(input.multiplier) : 1;
  const tlfAmount = Math.max(0, Math.round(finite(input.tlfAmountCents)));
  const plan = typeof input.plan === 'string' ? input.plan : '';
  const incomeDriven = INCOME_DRIVEN_PLANS.includes(plan);
  const pslf = input.pslf === true && incomeDriven;
  const newCohort = NEW_BORROWER_COHORTS.includes(input.firstLoanTime);

  const poverty =
    DEMO_POVERTY_GUIDELINE_CENTS.base +
    DEMO_POVERTY_GUIDELINE_CENTS.perAdditionalPerson * (householdSize - 1);
  const standardPayment = amortize(startBalance, 120, monthlyRate);
  const icrCap = amortize(startBalance, 144, monthlyRate);
  const ibrPercent = newCohort ? 0.1 : 0.15;
  const share = (annualCents: number) => Math.round((annualCents / 12) * multiplier);

  let baseTerm = 120;
  let incomePayment: ((agiCents: number) => number) | undefined;
  if (plan === 'extendedPlan') {
    baseTerm = 300;
  } else if (plan === 'ibr') {
    baseTerm = newCohort ? 240 : 300;
    incomePayment = (agiCents) =>
      Math.min(
        standardPayment,
        Math.max(0, share(ibrPercent * Math.max(0, agiCents - 1.5 * poverty)))
      );
  } else if (plan === 'paye') {
    baseTerm = 240;
    incomePayment = (agiCents) =>
      Math.min(standardPayment, Math.max(0, share(0.1 * Math.max(0, agiCents - 1.5 * poverty))));
  } else if (plan === 'icr') {
    baseTerm = 300;
    incomePayment = (agiCents) =>
      Math.min(icrCap, Math.max(0, share(0.2 * Math.max(0, agiCents - poverty))));
  } else if (plan === 'repaymentAssistancePlan') {
    baseTerm = 360;
    // The child deduction and $10 floor belong to the household payment, before this group's share.
    incomePayment = (agiCents) =>
      Math.round(
        Math.max(1000, Math.round((rapPercent(agiCents) * agiCents) / 12) - 5000 * children) *
          multiplier
      );
  }
  const term = pslf
    ? Math.max(1, 120 - paidMonths)
    : incomeDriven
      ? Math.max(1, baseTerm - paidMonths)
      : baseTerm;
  const fixedPayment = incomeDriven ? 0 : amortize(startBalance, term, monthlyRate);

  const rows: PaymentPlanRow[] = [];
  let balance = startBalance;
  let cumulative = 0;
  let tlfForgiven = 0;
  let remainderForgiven = 0;
  for (let month = 1; month <= term && balance > 0; month++) {
    const interest = Math.round(balance * monthlyRate);
    const agiThisMonth = Math.round(
      agi * Math.pow(1 + agiGrowthRate, Math.floor((month - 1) / 12))
    );
    const scheduled = incomePayment
      ? incomePayment(agiThisMonth)
      : month === term
        ? balance + interest
        : fixedPayment;
    const payment = Math.max(0, Math.min(scheduled, balance + interest));
    // RAP waives unpaid interest instead of capitalizing it.
    balance =
      plan === 'repaymentAssistancePlan'
        ? balance - Math.max(0, payment - interest)
        : balance + interest - payment;
    let forgiven = 0;
    if (tlfAmount > 0 && month === TLF_FORGIVENESS_MONTH && balance > 0) {
      const tlf = Math.min(Math.round(tlfAmount * multiplier), balance);
      tlfForgiven += tlf;
      forgiven += tlf;
      balance -= tlf;
    }
    if (month === term && balance > 0) {
      remainderForgiven = balance;
      forgiven += balance;
      balance = 0;
    }
    cumulative += payment;
    rows.push({
      month,
      payment,
      cumulative_payment: cumulative,
      employer_payment: 0,
      subsidy_amount: 0,
      forgiveness_amount: forgiven,
      balance: Math.max(0, balance),
    });
  }

  const payments = rows.map((row) => row.payment);
  return {
    rows: rows.length ? rows : [zeroRow()],
    summary: {
      plan,
      title: PLAN_TITLES[plan] ?? plan,
      tlf_amount: tlfAmount,
      pslf,
      first_month_payment: payments[0] ?? 0,
      max_monthly_payment: payments.length ? Math.max(...payments) : 0,
      total_payment: cumulative,
      employee_paid: cumulative,
      employer_paid: 0,
      forgiveness_paid: remainderForgiven + tlfForgiven,
      tlf_forgiveness: tlfForgiven,
      pslf_forgiveness: pslf ? remainderForgiven : 0,
      subsidy_paid: 0,
      num_payments: rows.length,
      paid_months_used: incomeDriven ? paidMonths : 0,
      paid_months_preserved: incomeDriven,
      warning: null,
    },
  };
}

function plansFor(input: SortPlansPayload, isPlus: boolean) {
  const plans = ['tenYearsStandard'];
  if (finite(input.current_balance) > EXTENDED_PLAN_MIN_BALANCE_CENTS) {
    plans.push('extendedPlan');
  }
  if (!isPlus) {
    if (input.last_loan_time === 'obbb_cohort') {
      plans.push('repaymentAssistancePlan');
    } else {
      plans.push('ibr');
      if (input.first_loan_time === 'new_ibr_cohort') {
        plans.push('paye');
      }
      plans.push('icr', 'repaymentAssistancePlan');
    }
  }
  if (input.current_plan && !plans.includes(input.current_plan)) {
    plans.push(input.current_plan);
  }
  return plans;
}

// Mirrors tio-api SortPlansEstimatorFilter, which trims Direct/FFEL output per requesting app.
function filterForApp(rows: SortedPlanRow[], input: SortPlansPayload, requestingApp: string) {
  if (input.loan_type !== 'direct' && input.loan_type !== 'ffel') {
    return rows;
  }
  const expectedPslf = input.current_pslf === true && isPslfEligiblePlanKey(input.current_plan);
  const isCurrent = (row: SortedPlanRow) =>
    row.plan === input.current_plan &&
    row.tlf_amount === finite(input.current_tlf_amount) &&
    row.pslf === expectedPslf;
  if (requestingApp === 'IDR-0726') {
    return rows.filter((row) => IDR_0726_PLANS.has(row.plan) || isCurrent(row));
  }
  if (requestingApp === 'PSLF-0726') {
    return rows.filter((row) => row.pslf || isCurrent(row));
  }
  return rows;
}

export function buildSortPlansOutput(
  input: SortPlansPayload,
  requestingApp: string
): SortPlansOutput {
  const isPlus = input.loan_type === 'plus';
  const tlfAmounts =
    !isPlus && Array.isArray(input.tlf_amounts) && input.tlf_amounts.length
      ? input.tlf_amounts
      : [0];
  const rows: SortedPlanRow[] = [];
  for (const plan of plansFor(input, isPlus)) {
    const pslfVariants =
      input.pslf_eligible === true && isPslfEligiblePlanKey(plan) ? [false, true] : [false];
    for (const tlfAmountCents of tlfAmounts) {
      for (const pslf of pslfVariants) {
        rows.push(
          simulatePlan({
            balanceCents: input.current_balance,
            rateAnnual: input.interest_rate_annual,
            agiCents: input.agi,
            agiGrowthRate: input.agi_growth_rate,
            adults: input.number_of_adults,
            children: input.number_of_children,
            paidMonths: input.current_paid_months,
            firstLoanTime: input.first_loan_time,
            lastLoanTime: input.last_loan_time,
            plan,
            pslf,
            tlfAmountCents,
            multiplier: input.multiplier,
          }).summary
        );
      }
    }
  }
  const plans = filterForApp(rows, input, requestingApp);
  if (input.sort_by === 'first_month_then_total') {
    plans.sort(
      (a, b) => a.first_month_payment - b.first_month_payment || a.total_payment - b.total_payment
    );
  } else if (input.sort_by === 'total_then_first_month') {
    plans.sort(
      (a, b) => a.total_payment - b.total_payment || a.first_month_payment - b.first_month_payment
    );
  }
  return {
    loan_type: input.loan_type,
    sort_by: input.sort_by,
    available_plans_warning: null,
    plans,
  };
}

export function simulateSchedule(
  payload: GeneratePaymentPlanPayload | undefined
): GeneratePaymentPlanResponse {
  if (typeof payload?.payment_plan !== 'string' || !payload.payment_plan) {
    return {
      rows: [],
      title: '',
      employee_paid: 0,
      employer_paid: 0,
      forgiveness_paid: 0,
      subsidy_paid: 0,
      final_balance: 0,
      is_error: true,
      error: 'demo: invalid payload',
    };
  }
  const { rows, summary } = simulatePlan({
    balanceCents: payload.current_balance,
    rateAnnual: payload.interest_rate_annual,
    agiCents: payload.agi,
    agiGrowthRate: payload.agi_growth_rate,
    adults: payload.number_of_adults,
    children: payload.number_of_children,
    paidMonths: payload.paid_months,
    firstLoanTime: payload.first_loan_time,
    lastLoanTime: payload.last_loan_time,
    plan: payload.payment_plan,
    pslf: payload.pslf,
    tlfAmountCents: payload.tlf_amount,
    multiplier: payload.multiplier ?? 1,
  });
  return {
    rows,
    title: summary.title,
    employee_paid: summary.employee_paid,
    employer_paid: 0,
    forgiveness_paid: summary.forgiveness_paid,
    subsidy_paid: 0,
    final_balance: 0,
    monthly_payment: summary.first_month_payment,
  };
}
